import { ADVANCED_ORDER_TYPE_LABELS, type AdvancedOrderType } from '@/constants/advancedOrder';

import { limitOrderBuildResponseSchema } from '@/utils/0x.schema';
import type { LimitOrderBuildResponse } from '@/utils/0x.types';
import { fetchWithJwt } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

/** The `/api/orderbook/build-order` request body. `maker` is omitted for a wallet-less preview. */
export type LimitOrderBuildBody = {
  makerAsset: string;
  takerAsset: string;
  makingAmount: string;
  takingAmount: string;
  maker?: string;
  chainId: number;
  expiration: string;
  makerDecimals: number;
  takerDecimals: number;
  orderType: AdvancedOrderType;
  triggerCrossPrice?: string;
};

export class LimitOrderBuildHttpError extends Error {
  constructor(
    readonly status: number,
    orderType: AdvancedOrderType,
    /** Upstream quote failure text forwarded by the route (`ORDERBOOK_QUOTE_FAILED`), when any. */
    readonly upstreamMessage?: string,
  ) {
    super(`Failed to build ${ADVANCED_ORDER_TYPE_LABELS[orderType]} order (${status})`);
    this.name = 'LimitOrderBuildHttpError';
  }
}

async function readUpstreamQuoteMessage(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { reason?: string; message?: string };
    return body.reason === 'ORDERBOOK_QUOTE_FAILED' ? body.message : undefined;
  } catch {
    return undefined;
  }
}

export function toLimitOrderBuildBody({
  makerAsset,
  takerAsset,
  makingAmount,
  takingAmount,
  maker,
  chainId,
  expiry,
  makerDecimals,
  takerDecimals,
  orderType,
  triggerCrossPrice,
}: Omit<LimitOrderBuildBody, 'maker' | 'expiration' | 'triggerCrossPrice'> & {
  maker: string | undefined;
  expiry: Date;
  triggerCrossPrice: string | undefined;
}): LimitOrderBuildBody {
  return {
    makerAsset,
    takerAsset,
    makingAmount,
    takingAmount,
    ...(maker === undefined ? {} : { maker }),
    chainId,
    expiration: Math.floor(expiry.getTime() / 1000).toString(),
    makerDecimals,
    takerDecimals,
    orderType,
    ...(triggerCrossPrice ? { triggerCrossPrice } : {}),
  };
}

export function isSameLimitOrderBuildBody(a: LimitOrderBuildBody, b: LimitOrderBuildBody): boolean {
  return (
    a.chainId === b.chainId &&
    a.maker === b.maker &&
    a.makerAsset === b.makerAsset &&
    a.takerAsset === b.takerAsset &&
    a.makingAmount === b.makingAmount &&
    a.takingAmount === b.takingAmount &&
    a.makerDecimals === b.makerDecimals &&
    a.takerDecimals === b.takerDecimals &&
    a.expiration === b.expiration &&
    a.orderType === b.orderType &&
    a.triggerCrossPrice === b.triggerCrossPrice
  );
}

export async function fetchLimitOrderBuild(
  body: LimitOrderBuildBody,
): Promise<LimitOrderBuildResponse> {
  const response = await fetchWithJwt(routes.api.LIMIT_ORDER_BUILD, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new LimitOrderBuildHttpError(
      response.status,
      body.orderType,
      await readUpstreamQuoteMessage(response),
    );
  }
  return limitOrderBuildResponseSchema.parse(await response.json());
}
